// ABOUTME: Full-height hover overlay on the right edge of ShDataGrid3 that reveals an add-column button and popover.
// ABOUTME: Not a grid column — it's an absolute-positioned sibling of the scroll container, invisible until hovered.

import { memo, useState, useCallback, useMemo, useRef } from "react";
import { Box, Popover } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@emotion/react";
import { tokens } from "../../../theme";
import { useTranslation } from "react-i18next";

/**
 * ShGridAddColumn
 *
 * A narrow hover strip that lives at the right edge of the grid area as an absolute
 * overlay. It is invisible until the user moves the cursor near the right edge; on
 * hover, the strip reveals a full-height add-column button. Clicking it opens a
 * popover listing every code column that isn't currently in runtime.
 *
 * The overlay is NOT a grid column. It sits above the grid as an absolute-positioned
 * sibling of the scroll container, so it doesn't occupy any grid track and doesn't
 * affect row layout.
 *
 * Props:
 *   hiddenColumns    — ordered array of code columns absent from runtime
 *   newColumnFields  — array of field names flagged as "new" since the saved config was created
 *   onAdd            — (field: string) => void
 *   onAcknowledge    — () => void, called on popover open to clear the indicator
 */
const ShGridAddColumn = memo(function ShGridAddColumn({
    hiddenColumns = [],
    newColumnFields = [],
    onAdd,
    onAcknowledge,
}) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {t} = useTranslation();

    const [hovered, setHovered] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const hoverZoneRef = useRef(null);

    const newFieldSet = useMemo(() => new Set(newColumnFields || []), [newColumnFields]);
    const hasNew = newFieldSet.size > 0;

    const orderedHidden = useMemo(() => {
        if (!Array.isArray(hiddenColumns)) return [];
        if (!hasNew) return hiddenColumns;
        const newOnes = [];
        const rest = [];
        for (const col of hiddenColumns) {
            if (newFieldSet.has(col.field)) newOnes.push(col);
            else rest.push(col);
        }
        return [...newOnes, ...rest];
    }, [hiddenColumns, newFieldSet, hasNew]);

    const handleMouseEnter = useCallback(() => setHovered(true), []);
    const handleMouseLeave = useCallback(() => {
        // Keep hovered state while popover is open so the reveal bar doesn't flicker away.
        if (!anchorEl) setHovered(false);
    }, [anchorEl]);

    const handleOpen = useCallback(
        (event) => {
            event.stopPropagation();
            setAnchorEl(hoverZoneRef.current);
            if (onAcknowledge) onAcknowledge();
        },
        [onAcknowledge]
    );

    const handleClose = useCallback(() => {
        setAnchorEl(null);
        setHovered(false);
    }, []);

    const handlePick = useCallback(
        (field) => {
            if (onAdd) onAdd(field);
            setAnchorEl(null);
            setHovered(false);
        },
        [onAdd]
    );

    const open = Boolean(anchorEl);
    const revealed = hovered || open;

    return (
        <>
            <Box
                ref={hoverZoneRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleOpen}
                role="button"
                tabIndex={0}
                aria-label="Add column"
                className="sh-grid-add-column-hover"
                sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: revealed ? 40 : 16,
                    cursor: "pointer",
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "width 150ms ease, background 150ms ease",
                    background: revealed
                        ? theme.palette.mode === "dark"
                            ? colors.grey[300]
                            : colors.bg.tertiary
                        : "transparent",
                    borderLeft: revealed
                        ? `1px solid ${colors.grey[200]}`
                        : "1px solid transparent",
                    "@media screen and (max-width: 47.9375em)": {
                        display: "none",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        position: "relative",
                        background: revealed
                            ? theme.palette.mode === "dark"
                                ? colors.grey[400]
                                : colors.grey[100]
                            : "transparent",
                        color: colors.txt.primary,
                        opacity: revealed ? 1 : 0,
                        transition: "opacity 150ms ease",
                    }}
                >
                    <AddIcon fontSize="small" />
                    {hasNew && (
                        <Box
                            aria-hidden="true"
                            sx={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.blue[400]
                                        : colors.primary[400],
                                border: `1px solid ${
                                    theme.palette.mode === "dark"
                                        ? colors.grey[400]
                                        : colors.grey[100]
                                }`,
                            }}
                        />
                    )}
                </Box>
                {/* Persistent "new" dot on the 16px collapsed strip, so users know new columns exist
                    even before they hover. */}
                {hasNew && !revealed && (
                    <Box
                        aria-hidden="true"
                        sx={{
                            position: "absolute",
                            top: 12,
                            right: 4,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background:
                                theme.palette.mode === "dark"
                                    ? colors.blue[400]
                                    : colors.primary[400],
                        }}
                    />
                )}
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "center", horizontal: "left" }}
                transformOrigin={{ vertical: "center", horizontal: "right" }}
                slotProps={{
                    paper: {
                        sx: {
                            mr: 1,
                            minWidth: 240,
                            maxHeight: 360,
                            overflow: "auto",
                            background:
                                theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                            borderRadius: 3,
                            boxShadow: "0px 2px 12px rgba(49, 65, 95, 0.25)",
                        },
                    },
                }}
            >
                {orderedHidden.length === 0 ? (
                    <Box
                        sx={{
                            py: 2,
                            fontSize: 14,
                            color: colors.txt.secondary,
                            fontFamily: "Roboto, sans-serif",
                            textAlign: "center",
                        }}
                    >
                        {t("no_hidden_columns")}
                    </Box>
                ) : (
                    <Box sx={{ py: 0.5 }}>
                        {orderedHidden.map((col) => {
                            const isNew = newFieldSet.has(col.field);
                            return (
                                <Box
                                    key={col.field}
                                    onClick={() => handlePick(col.field)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        px: 2,
                                        height: 40,
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontFamily: "Roboto, sans-serif",
                                        color: colors.txt.primary,
                                        "&:hover": {
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? colors.grey[400]
                                                    : colors.bg.tertiary,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            minWidth: 0,
                                        }}
                                    >
                                        {col.headerName ?? col.field}
                                    </Box>
                                    {isNew && (
                                        <Box
                                            sx={{
                                                ml: 1,
                                                flexShrink: 0,
                                                px: 1,
                                                py: 0.25,
                                                fontSize: 11,
                                                fontWeight: 600,
                                                borderRadius: 2,
                                                textTransform: "uppercase",
                                                background:
                                                    theme.palette.mode === "dark"
                                                        ? colors.blue[100]
                                                        : colors.primary[100],
                                                color:
                                                    theme.palette.mode === "dark"
                                                        ? colors.blue[400]
                                                        : colors.primary[400],
                                            }}
                                        >
                                            New
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Popover>
        </>
    );
});

export default ShGridAddColumn;
